import { useMemo } from 'react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import {
  findWebhookProviderCatalogEntry,
  useWebhookProviderCatalog,
} from '@cotera/client/app/v1/webhook-providers';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import { useWorkflowModel } from '../workflow.model';
import type { WorkflowGraphApiData } from '../workflow-graph.types';

const {
  PersistedWorkflowTriggerKind,
  buildWebhookRunInput,
  buildCronRunInput,
  CRON_RUN_INPUT_SAMPLE_TIMESTAMP,
} = PersistedWorkflow;

function resolveEnabledBinding(graph: WorkflowGraphApiData) {
  return (
    graph.triggerBindings.find((binding) => binding.enabled === true) ??
    graph.triggerBindings[0]
  );
}

function resolveWebhookRunInputSample(
  graph: WorkflowGraphApiData,
  providers: ReturnType<typeof useWebhookProviderCatalog>['providers']
): unknown | null {
  const binding = resolveEnabledBinding(graph);
  if (binding === undefined) {
    return null;
  }
  const snapshot = graph.triggers.find(
    (row) => row.trigger.id === binding.triggerId
  );
  if (
    snapshot === undefined ||
    snapshot.kind !== PersistedWorkflowTriggerKind.Webhook
  ) {
    return null;
  }
  const providerKey = snapshot.trigger.providerKey;
  const eventKey = snapshot.trigger.eventKey;
  if (providerKey === undefined || eventKey === undefined) {
    return null;
  }
  const catalogEntry = findWebhookProviderCatalogEntry(providers, providerKey);
  const event = catalogEntry?.events.find(
    (candidate) => candidate.key === eventKey
  );
  if (event?.samplePayload === undefined) {
    return null;
  }
  return buildWebhookRunInput(binding.defaultInput ?? {}, event.samplePayload);
}

function resolveCronRunInputSample(
  graph: WorkflowGraphApiData
): unknown | null {
  const binding = resolveEnabledBinding(graph);
  if (binding === undefined) {
    return null;
  }
  const snapshot = graph.triggers.find(
    (row) => row.trigger.id === binding.triggerId
  );
  if (
    snapshot === undefined ||
    snapshot.kind !== PersistedWorkflowTriggerKind.Cron
  ) {
    return null;
  }
  const defaultInput = binding.defaultInput;
  const baseInput =
    defaultInput !== undefined &&
    defaultInput !== null &&
    typeof defaultInput === 'object' &&
    !Array.isArray(defaultInput)
      ? defaultInput
      : {};
  const triggerInput =
    snapshot.trigger.workflowInput !== undefined &&
    snapshot.trigger.workflowInput !== null &&
    typeof snapshot.trigger.workflowInput === 'object' &&
    !Array.isArray(snapshot.trigger.workflowInput)
      ? snapshot.trigger.workflowInput
      : null;
  const mergedInput =
    Object.keys(baseInput).length > 0 ? baseInput : triggerInput ?? {};
  return buildCronRunInput(mergedInput, CRON_RUN_INPUT_SAMPLE_TIMESTAMP);
}

/**
 * Sample workflow run input for the active cron or webhook trigger, used to
 * suggest JSON Pointer paths for idempotency keys.
 */
export function useWorkflowRunInputSample(): {
  readonly sample: unknown | null;
  readonly triggerKind: 'cron' | 'webhook' | null;
} {
  const model = useWorkflowModel();
  const graph = useWatchableValue(model.graphWatchable);
  const { providers } = useWebhookProviderCatalog();

  return useMemo(() => {
    if (graph === null) {
      return { sample: null, triggerKind: null };
    }
    const binding = resolveEnabledBinding(graph);
    if (binding === undefined) {
      return { sample: null, triggerKind: null };
    }
    const snapshot = graph.triggers.find(
      (row) => row.trigger.id === binding.triggerId
    );
    if (snapshot?.kind === PersistedWorkflowTriggerKind.Webhook) {
      return {
        sample: resolveWebhookRunInputSample(graph, providers),
        triggerKind: 'webhook' as const,
      };
    }
    if (snapshot?.kind === PersistedWorkflowTriggerKind.Cron) {
      return {
        sample: resolveCronRunInputSample(graph),
        triggerKind: 'cron' as const,
      };
    }
    return { sample: null, triggerKind: null };
  }, [graph, providers]);
}
